import React from 'react'
import { Experience, ExperienceOption, Order, Price } from '@/types'
import { format, isWithinInterval, parseISO, setYear } from 'date-fns'

export const sumOrderAmounts = (
  orders: Pick<Order, 'amount'>[] | undefined | null
): number => (orders ?? []).reduce((sum, order) => sum + order.amount, 0)

/**
 * DEV-801: find a cart line that duplicates the booking the guest is about to
 * add, i.e. same option on the same day and time slot. Used to warn before a
 * second identical line is created: the embedded widget gives weak "added"
 * feedback, so guests were re-adding the same booking and paying twice.
 *
 * Matching is intentionally conservative (fails safe): a missing option id,
 * date or `startsAt` yields "no duplicate" so a legitimate add is never blocked.
 * Different dates or slots are NOT duplicates, so genuine multi-day carts still
 * work.
 */
export const findDuplicateCartOrder = (
  orders: Order[] | undefined,
  optionId: number | string | undefined,
  date: Date | undefined,
  slot: string | undefined
): Order | undefined => {
  if (!orders?.length || optionId == null || !date) {
    return undefined
  }

  const dateKey = format(date, 'yyyy-MM-dd')

  return orders.find((order) => {
    if (!order.startsAt || Number(order.option?.id) !== Number(optionId)) {
      return false
    }

    const orderStartsAt = parseISO(order.startsAt)

    if (format(orderStartsAt, 'yyyy-MM-dd') !== dateKey) {
      return false
    }

    if (!slot) {
      return true
    }

    return format(orderStartsAt, 'HH:mm') === slot || order.displayTime === slot
  })
}

export const getSeasonalPrice = (
  price: Price,
  targetDate: Date | undefined
) => {
  if (!price) {
    return 0
  }

  if (!targetDate) {
    return price.retailRate
  }

  // get the seasonalPrices from price, and them find wich seasonalPrice is matched the selectedDate
  const seasonalPrice = price?.seasonalPrices.find((seasonalPrice) => {
    // set the year to same as seasonalPrices
    const startsAt = setYear(seasonalPrice.startsAt, targetDate.getFullYear())
    const endsAt = setYear(seasonalPrice.endsAt, targetDate.getFullYear())

    return isWithinInterval(targetDate, { start: startsAt, end: endsAt })
  })

  if (seasonalPrice) {
    return seasonalPrice.retailRate
  }

  return price.retailRate
}

export const getExperienceOptionPriceRange = (option: ExperienceOption) => {
  let minPrice = Number.MAX_SAFE_INTEGER
  let maxPrice = Number.MIN_SAFE_INTEGER
  let isAllFree = true
  let allHideFreeLabel = true
  let minUnit,
    maxUnit = null

  option.prices?.forEach((price) => {
    if (price.isFree || price.retailRate < 0.01) {
      if (!price.hideFreeLabel) {
        allHideFreeLabel = false
      }
      return
    }

    if (price.isExtra) {
      return
    }

    if (price.retailRate < 0.01) {
      return
    }

    isAllFree = false

    if (price.retailRate < minPrice) {
      minPrice = price.retailRate
      minUnit = price.unitName
    }

    if (price.retailRate > maxPrice) {
      maxPrice = price.retailRate
      maxUnit = price.unitName
    }

    // count the seasonal price
    price.seasonalPrices?.forEach((seasonalPrice) => {
      if (seasonalPrice.retailRate < minPrice) {
        minPrice = seasonalPrice.retailRate
        minUnit = price.unitName
      }

      if (seasonalPrice.retailRate > maxPrice) {
        maxPrice = seasonalPrice.retailRate
        maxUnit = price.unitName
      }
    })
  })

  if (isAllFree) {
    return { min: 0, max: 0, maxUnit, minUnit, isAllFree, allHideFreeLabel }
  }

  return { min: minPrice ?? 0, max: maxPrice ?? 0, maxUnit, minUnit, isAllFree, allHideFreeLabel: false }
}

export function shouldHideFreeLabel(prices: Price[]): boolean {
  const freePrices = prices.filter((p) => p.isFree)
  return freePrices.length > 0 && freePrices.every((p) => p.hideFreeLabel)
}

/**
 * DEV-462 — pick which price affordance an experience surface should
 * render: a "Free" badge, a price range, or nothing at all.
 *
 * "none" applies when the experience has no rates (drafts, information
 * pages) or every free rate hides the "Free" label by configuration —
 * a non-bookable item must not advertise a price.
 */
export type ExperiencePriceDisplay = 'free' | 'range' | 'none'

export function getExperiencePriceDisplay(
  experience: Experience,
  priceRange: { isAllFree: boolean; allHideFreeLabel: boolean }
): ExperiencePriceDisplay {
  if (!experience.options?.length) return 'none'
  if (experience.isInformationPage) return 'none'

  if (priceRange.isAllFree) {
    return priceRange.allHideFreeLabel ? 'none' : 'free'
  }

  return 'range'
}

export const getExperiencePriceRange = (experience: Experience) => {
  let minPrice = Number.MAX_SAFE_INTEGER
  let maxPrice = Number.MIN_SAFE_INTEGER
  let minUnit,
    maxUnit = null

  let isAllFree = true
  let allHideFreeLabel = true

  experience.options?.forEach((option) => {
    const {
      min,
      max,
      maxUnit: optionMaxUnit,
      minUnit: optionMinUnit,
      isAllFree: optionIsAllFree,
      allHideFreeLabel: optionAllHideFreeLabel,
    } = getExperienceOptionPriceRange(option)

    if (!optionIsAllFree) {
      isAllFree = false

      if (min < minPrice) {
        minPrice = min
        minUnit = optionMinUnit
      }

      if (max > maxPrice) {
        maxPrice = max
        maxUnit = optionMaxUnit
      }
    }

    if (!optionAllHideFreeLabel) {
      allHideFreeLabel = false
    }
  })

  if (isAllFree) {
    return { min: 0, max: 0, maxUnit, minUnit, isAllFree, allHideFreeLabel }
  }

  return { min: minPrice ?? 0, max: maxPrice ?? 0, maxUnit, minUnit, isAllFree, allHideFreeLabel: false }
}

export function isEmail(email: string): boolean {
  var regEmail = new RegExp(
    '^[0-9a-z._-]+@{1}[0-9a-z.-]{2,}[.]{1}[a-z]{2,5}$',
    'i'
  )
  return regEmail.test(email)
}

export function isFreeExperience(experience: Experience): boolean {
  return experience.options.every((option) =>
    option.prices.every((price) => price.isFree)
  )
}

export const isInIframe = () => {
  return typeof window !== 'undefined' && window.self !== window.top
}

// Hook version that prevents hydration mismatches
export const useIsInIframe = () => {
  const [inIframe, setInIframe] = React.useState(false)

  React.useEffect(() => {
    setInIframe(isInIframe())
  }, [])

  return inIframe
}

export function parseJwt(token: string) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
      .join('')
  )
  return JSON.parse(jsonPayload)
}
